import { NonAppNavWrapper } from '@/components/generic/non-app-nav'
import ToolLinks from '@/components/page-specific/tools/tool-links'
import { useAuth, useUser } from '@clerk/nextjs'
import { ArrowRight, Check } from 'lucide-react'
import { NextSeo } from 'next-seo'
import { Inter } from 'next/font/google'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'

const inter = Inter({ subsets: ['latin'] })

export default function PDFSummarizer() {
  const router = useRouter()
  const { isSignedIn } = useAuth()

  const { user } = useUser()
  const isSubscribed = user?.publicMetadata.isSubscribed

  return (
    <>
      <NextSeo
        title="v0 report - The Leading AI PDF Summarizer"
        description="Discover v0 Report, the leading AI PDF summarizer tool for accurate and concise summaries. Simplify your PDF document reading in just one click."
      />
      <NonAppNavWrapper>
        <main className={`scroll-smooth flex min-h-screen flex-col ${inter.className}`}>
          <div className=" mt-36">
            <div className="mx-auto text-center">
              <h1 className="text-6xl text-zinc-700  grid gap-2">
                <span className="font-medium">v0 Report - Enhance Your Workflow with AI</span>
                <span className="font-thin italic font-serif leading-none text-yellow-600">
                  PDF Summarizer Tool
                </span>
              </h1>
            </div>
            <div className="ml-auto flex px-4 md:px-0 md:w-3/4 mx-auto mb-24">
              <div className="w-full mx-auto">
                <div className="grid gap-6">
                  <h2 className="text-4xl text-zinc-700 text-left mt-48">
                    <span className="font-medium">A Smarter Approach to </span>
                    <span className="font-thin italic font-serif leading-none text-sky-600">
                      Summarizing PDFs
                    </span>
                  </h2>
                  <p className="text-lg text-zinc-600 grid gap-2">
                    <span>
                      v0 Report is a powerful AI PDF analyzer tool that uses artificial intelligence
                      to concisely summarise content, including PDFs, documents, articles, audio
                      files, and presentations.
                    </span>
                    <span>
                      Say goodbye to sifting through pages of information or listening to long
                      recordings to find key points. Simply upload your files, and let the v0 Report
                      do the work.
                    </span>
                    <span>
                      The AI-powered PDF generator reads through the file, identifies the main
                      themes and essential details, and produces a summary that captures the essence
                      of your PDF content.
                    </span>
                  </p>
                  <Link
                    className="w-fit h-10 rounded-md px-4 inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-sky-600 text-primary-foreground hover:bg-sky-600/90 "
                    href={'/auth/signup'}
                  >
                    {' '}
                    Get started <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </div>
                <div className="grid gap-6 mt-24">
                  <Image
                    className="rounded-2xl w-full md:w-3/4"
                    src="/images/tools/report.webp"
                    alt="Image"
                    width={500}
                    height={300}
                  />

                  <h2 className="text-3xl text-zinc-700 text-left mt-8 font-medium">
                    How To Use v0 Report AI To Summarize A PDF
                  </h2>
                  <div className="mx-auto rounded-xl max-w-screen-lg p-1">
                    <div className="grid grid-cols-1 gap-8 mx-auto mb-24">
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Upload Your PDF</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Visit the v0 Report website and navigate to the PDF summarization tool.
                            Click the upload button and select the PDF file you want to summarize
                            from your device.
                          </p>{' '}
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Input Your Requirements</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Choose the summary length and any specific areas of focus (e.g., key
                            points, themes). Adjust any additional settings to tailor the summary to
                            your needs.
                          </p>{' '}
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Generate Your Sentences</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Let our v0 Report work its magic. In just a few seconds, you'll have a
                            high-quality, and engaging sentence for review.
                          </p>{' '}
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Review and Edit</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            While v0 Report strives for perfection, we understand the importance of
                            personalization. You can review and make any necessary edits to ensure
                            that the sentences generated meet your standards.
                          </p>{' '}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="grid gap-6 mt-24">
                  <Image
                    className="rounded-2xl w-full md:w-3/4"
                    src="/images/tools/on-laptop.webp"
                    alt="Image"
                    width={500}
                    height={300}
                  />

                  <h2 className="text-3xl text-zinc-700 text-left mt-8 font-medium">
                    Here's how it can help
                  </h2>
                  <div className="mx-auto rounded-xl max-w-screen-lg p-1">
                    <div className="grid grid-cols-1 gap-8 mx-auto mb-24">
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">
                            Quick and Efficient Summarization
                          </h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Save valuable time by generating concise summaries of lengthy PDFs in
                            seconds, allowing you to focus on more critical tasks.
                          </p>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Accurate Key Point Extraction</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Our AI technology accurately captures the main themes and essential
                            details, ensuring you get reliable and informative summaries every time.
                          </p>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Supports Various Content Types</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Besides PDFs, v0 Report can summarize documents, articles, audio files,
                            and presentations, making it a versatile tool for all your summarization
                            needs.
                          </p>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Easy-to-Use Interface</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Its user-friendly design, v0 Report makes it simple for anyone to upload
                            files and obtain summaries with just a few clicks.
                          </p>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Boosted Productivity</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            By providing clear and concise summaries, v0 Report helps you stay
                            informed and make quicker decisions, significantly enhancing your
                            overall productivity.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="grid gap-6 mt-24">
                  <h2 className="text-3xl text-zinc-700 text-left mt-8 font-medium">
                    The AI PDF Summarizer Tool Preferred by Industry Leaders
                  </h2>
                  <div className="mx-auto rounded-xl max-w-screen-lg p-1">
                    <div className="grid grid-cols-1 gap-8 mx-auto mb-24">
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Students and Researchers</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Simplify the process of reviewing academic papers, research articles,
                            and textbooks by quickly extracting key points and essential
                            information.
                          </p>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Business Professionals</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Streamline the analysis of reports, market research, and business
                            documents to make informed decisions without sifting through lengthy
                            PDFs.
                          </p>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Journalists and Writers</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Efficiently summarize interviews, articles, and reports to focus on
                            writing and content creation without spending excessive time on reading.
                          </p>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Legal Experts</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Quickly summarize legal documents, case studies, and contracts to get an
                            overview of important details and expedite the review process.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="grid gap-6 mt-24">
                  <h2 className="text-3xl text-zinc-700 text-left mt-8 font-medium">
                    Why Choose the v0 AI Report?
                  </h2>
                  <div className="mx-auto rounded-xl max-w-screen-lg p-1">
                    <div className="grid grid-cols-1 gap-8 mx-auto mb-24">
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Accurate AI PDF Summary</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            v0 Report offers an accurate AI PDF summary tool designed to help you
                            quickly grasp the main points of lengthy documents. It meticulously
                            analyzes your PDFs and essential details to create concise and precise
                            summaries.
                          </p>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Time-Efficient Summarization</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Using v0 Report's AI PDF tool to summarize text, articles, documents, or
                            audio files significantly reduces the time you spend reading or
                            listening to lengthy content. This saved time can then be redirected to
                            other productive tasks, boosting your overall efficiency.
                          </p>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Multi-Format Support</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            v0 Report's AI summary generator supports various document file formats,
                            including PDF, PDF with OCR, .docx, .doc, .txt, .csv, .rtf, .odt, .ods,
                            .odp, .pptx, and .html files. It also supports audio and multimedia
                            files such as .mp3, .mp4, .wav, .webm, .flac, .oga, .ogg, and .mpeg.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="hidden sm:block">
                <ToolLinks />
              </div>
            </div>
            <div className="w-fit grid gap-8 bg-sky-50 rounded-xl py-8 md:py-20 px-5 md:px-24 mx-2 md:mx-auto">
              <p className="text-2xl font-semibold text-gray-900 ">Get Started Today!</p>
              <p className="text-xl tracking-tight font-medium text-zinc-600 grid gap-2">
                <span>
                  Embrace the efficiency of the automated AI report writing tool with the v0 AI
                  Report Generator.
                </span>
                <span>
                  Sign up now or request a demo to see how our report writer tool can revolutionize
                  how you work with your report data.{' '}
                </span>
                <span>Transform your report analysis with v0 Report's accuracy and speed!</span>
              </p>
            </div>
            <div className="w-11/12 mx-auto my-24 md:w-fit text-center grid gap-8">
              <Link
                rel="canonical"
                href={isSubscribed ? '/home' : '/'}
                className="flex justify-center w-fit mx-auto"
              >
                <Image
                  className="object-scale-down h-8 w-8"
                  src="/images/logo.png"
                  alt="Logo"
                  width={200}
                  height={300}
                />
                <p className="my-auto ml-4 hidden sm:block bg-gray-100 text-gray-600 px-3 py-1.5 rounded ">
                  v0 report
                </p>
              </Link>
              <h2 className="text-3xl tracking-tight font-semibold leading-tight text-gray-900 ">
                Experience the future of document generation today <br />
                with v0 Report!
              </h2>
              <Link
                className="w-fit mx-auto h-8 rounded-md px-4 inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-sky-600 text-primary-foreground hover:bg-sky-600/90 "
                href={'/auth/signup'}
              >
                Get started
              </Link>
            </div>
          </div>
        </main>
      </NonAppNavWrapper>
    </>
  )
}
